
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CompanyPeriodSelector from '../../../shared/components/CompanyPeriodSelector';
import PageHeader from '../../../shared/components/PageHeader';
import { fetchStandardRatios } from '../../../services/analysisRatiosStandard';
import { fetchRatioTrend } from '../../../services/analysisRatioTrend';
import { fetchSectorRatioAnalysis } from '../../../services/analysisSectorRatio';
import { fetchSavedAiFinancialReport, fetchSavedAiFinancialReports } from '../../../services/analysisAiReport';
import { useLanguage } from '../../../app/i18n/LanguageContext';
import { exportDashboardPdf } from './dashboardPdfExport';
import './FinancialAnalysisDashboardPage.css';

const baseEnvironment = process.env.REACT_APP_API_BASE_URL?.trim() || 'http://localhost:8010';
const API_BASE = baseEnvironment.replace(/\/$/, '');

const buildHeaders = () => {
  const token = sessionStorage.getItem('authToken');
  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const UI_TEXT = {
  'tr-TR': {
    pageTitle: 'Finansal Yonetim Dashboard',
    pageDescription: 'CEO ve CFO icin performans, risk ve aksiyon gorunumu.',
    refreshing: 'Guncelleniyor...',
    exportingPdf: 'PDF olusturuluyor...',
    exportPdf: 'PDF Al',
    refreshDashboard: 'Dashboard Guncelle',
    reportingBasis: 'Raporlama Bazi',
    ifrsDefault: 'IFRS (Varsayilan)',
    vukOption: 'VUK',
    activeBasis: 'Analiz Bazi',
    basisMismatchNotice: 'Sektor kartinda sirket oranlari IFRS, benchmark ise VUK/EVDS bazindadir.',
    periodsLoadError: 'Donemler yuklenemedi.',
    dashboardLoadError: 'Dashboard verileri yuklenemedi.',
    strong: 'Guclu',
    balanced: 'Dengeli',
    risky: 'Riskli',
    noData: 'Veri yok',
    lowRisk: 'Dusuk risk',
    caution: 'Dikkat',
    alarm: 'Alarm',
    overallHealthScore: 'Genel Saglik Skoru',
    aboveStandardDensity: 'Standart Ustu Rasyo Yogunlugu',
    aboveStandardBand: 'Standart ustu rasyo bandi',
    leverageRisk: 'Kaldirac Riski',
    leverageThermo: 'Kaldirac risk termometresi',
    company: 'Sirket',
    standard: 'Std',
    aiScore: 'AI Skor',
    verdictLabel: 'Verdict',
    criticalRisk: 'Kritik Risk',
    noTrendData: 'Trend verisi yok.',
    periodTrendAnalysisAria: 'Donem trend analizi',
    diff: 'Fark',
    criticalDeviations: 'Kritik Sapmalar',
    top5Deviation: 'Top 5 rasyo sapmasi',
    noDeviationData: 'Sapma verisi bulunmuyor.',
    companyVsStandard: 'Sirket / Standart Karsilastirmasi',
    sortedByDeviation: 'Sapma buyuklugune gore siralandi',
    noCompareData: 'Karsilastirma verisi yok.',
    periodTrendAnalysis: 'Donem Trend Analizi',
    ratio: 'Rasyo',
    flat: 'Yatay',
    strengthening: 'Gucleniyor',
    weakening: 'Zayifliyor',
    sectorBenchmark: 'Sektorel Benchmark',
    noSectorData: 'Sektor verisi bulunmuyor.',
    percentileInSector: 'Sektorde {pct}. yuzdelik dilim',
    sectorAvg: 'Sektor Ort.',
    aiVerdictAndAction: 'AI Verdict ve Aksiyon Etkisi',
    aiExecutiveSnapshot: 'AI Executive Snapshot',
    aiVerdictCard: 'AI Verdict Karti',
    driverAnalysis: 'Surucu Analizi',
    strengthVsWeakness: 'Guclu ve Zayif Yonler',
    actionImpactProjection: 'Aksiyon Etki Projeksiyonu',
    lastAiReport: 'Son AI raporu',
    confidence: 'Confidence',
    mostCriticalRisk: 'En kritik risk',
    aiReportTextMissing: 'AI rapor metni bulunmuyor.',
    actionPriorityMissing: 'Aksiyon onceligi bulunmuyor.',
    scoreBoost: 'Skor +',
    weaknesses: 'Zayif',
    dataQualityIndicator: 'Veri Kalite Gostergesi',
    trustScore: 'Guven Skoru',
    missingData: 'Eksik Veri',
    suspiciousData: 'Supheli Veri',
    aiRecordCount: 'AI Kayit Sayisi',
    standardAnalysis: 'Standart Analiz',
    trendAnalysis: 'Trend Analizi',
    sectorAnalysis: 'Sektorel Analiz',
    aiReportDetail: 'AI Rapor Detayi'
  },
  'en-US': {
    pageTitle: 'Financial Management Dashboard',
    pageDescription: 'Performance, risk and action view for CEOs and CFOs.',
    refreshing: 'Refreshing...',
    exportingPdf: 'Building PDF...',
    exportPdf: 'Export PDF',
    refreshDashboard: 'Refresh Dashboard',
    reportingBasis: 'Reporting Basis',
    ifrsDefault: 'IFRS (Default)',
    vukOption: 'VUK',
    activeBasis: 'Analysis Basis',
    basisMismatchNotice: 'Sector card compares IFRS company ratios against VUK/EVDS benchmark.',
    periodsLoadError: 'Periods could not be loaded.',
    dashboardLoadError: 'Dashboard data could not be loaded.',
    strong: 'Strong',
    balanced: 'Balanced',
    risky: 'Risky',
    noData: 'No data',
    lowRisk: 'Low risk',
    caution: 'Caution',
    alarm: 'Alarm',
    overallHealthScore: 'Overall Health Score',
    aboveStandardDensity: 'Above Standard Ratio Density',
    aboveStandardBand: 'Above-standard ratio band',
    leverageRisk: 'Leverage Risk',
    leverageThermo: 'Leverage risk thermometer',
    company: 'Company',
    standard: 'Std',
    aiScore: 'AI Score',
    verdictLabel: 'Verdict',
    criticalRisk: 'Critical Risk',
    noTrendData: 'No trend data.',
    periodTrendAnalysisAria: 'Period trend analysis',
    diff: 'Diff',
    criticalDeviations: 'Critical Deviations',
    top5Deviation: 'Top 5 ratio deviations',
    noDeviationData: 'No deviation data found.',
    companyVsStandard: 'Company / Standard Comparison',
    sortedByDeviation: 'Sorted by deviation magnitude',
    noCompareData: 'No comparison data.',
    periodTrendAnalysis: 'Period Trend Analysis',
    ratio: 'Ratio',
    flat: 'Flat',
    strengthening: 'Strengthening',
    weakening: 'Weakening',
    sectorBenchmark: 'Sector Benchmark',
    noSectorData: 'No sector data found.',
    percentileInSector: '{pct}th percentile in sector',
    sectorAvg: 'Sector Avg.',
    aiVerdictAndAction: 'AI Verdict and Action Impact',
    aiExecutiveSnapshot: 'AI Executive Snapshot',
    aiVerdictCard: 'AI Verdict Card',
    driverAnalysis: 'Driver Analysis',
    strengthVsWeakness: 'Strength vs Weakness',
    actionImpactProjection: 'Action Impact Projection',
    lastAiReport: 'Last AI report',
    confidence: 'Confidence',
    mostCriticalRisk: 'Most critical risk',
    aiReportTextMissing: 'AI report text not found.',
    actionPriorityMissing: 'No prioritized action found.',
    scoreBoost: 'Score +',
    weaknesses: 'Weak',
    dataQualityIndicator: 'Data Quality Indicator',
    trustScore: 'Trust Score',
    missingData: 'Missing Data',
    suspiciousData: 'Suspicious Data',
    aiRecordCount: 'AI Record Count',
    standardAnalysis: 'Standard Analysis',
    trendAnalysis: 'Trend Analysis',
    sectorAnalysis: 'Sector Analysis',
    aiReportDetail: 'AI Report Detail'
  }
};

const RATIO_TITLE_TRANSLATIONS = {
  debt_to_equity_ratio: 'Debt to Equity Ratio',
  return_on_equity: 'Return on Equity (ROE)',
  return_on_assets: 'Return on Assets (ROA)',
  net_profit_margin: 'Net Profit Margin'
};

const API_TEXT_REPLACEMENTS_EN = [
  ['Guclu', 'Strong'],
  ['Dengeli', 'Balanced'],
  ['Riskli', 'Risky'],
  ['Dusuk risk', 'Low risk'],
  ['Kritik risk', 'Critical risk'],
  ['Kritik Risk', 'Critical Risk'],
  ['Kaldirac', 'Leverage'],
  ['Borc/Ozk', 'Debt/Equity'],
  ['Borc/Ozkaynak', 'Debt/Equity'],
  ['Operasyonel verimlilik', 'Operational efficiency'],
  ['Likidite', 'Liquidity'],
  ['Nakit', 'Cash'],
  ['Karlilik', 'Profitability'],
  ['Satis', 'Sales'],
  ['Maliyet', 'Cost'],
  ['Sektor', 'Sector'],
  ['Sirket', 'Company'],
  ['Donem', 'Period'],
  ['Aksiyon', 'Action'],
  ['oncelik', 'priority']
];

const translateApiText = (value, language = 'tr-TR') => {
  if (!value || !language.startsWith('en')) {
    return value;
  }
  let output = String(value);
  API_TEXT_REPLACEMENTS_EN.forEach(([source, target]) => {
    output = output.replace(new RegExp(source, 'gi'), target);
  });
  return output;
};

const humanizeRatioKey = (key) => (key || '')
  .split('_')
  .filter(Boolean)
  .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
  .join(' ');

const formatNumber = (value, locale = 'tr-TR', digits = 2) => {
  const n = Number(value);
  if (Number.isNaN(n)) {
    return '-';
  }
  return n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

const formatSigned = (value, locale = 'tr-TR', digits = 1) => {
  const n = Number(value);
  if (Number.isNaN(n)) {
    return '-';
  }
  return `${n > 0 ? '+' : ''}${n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;
};

const formatPercent = (value, locale = 'tr-TR', digits = 1) => {
  const n = Number(value);
  if (Number.isNaN(n)) {
    return '-';
  }
  return `${n.toLocaleString(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits })}%`;
};

const formatDateTime = (value, locale = 'tr-TR') => {
  if (!value) {
    return '-';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleString(locale);
};

const calculateDeviation = (value, standard) => {
  const v = Number(value);
  const s = Number(standard);
  if (Number.isNaN(v) || Number.isNaN(s) || Math.abs(s) < 0.000001) {
    return null;
  }
  return ((v - s) / Math.abs(s)) * 100;
};

const summarizeAiText = (text) => {
  if (!text) {
    return [];
  }
  return text
    .replace(/\n+/g, ' ')
    .split(/(?<=[.!?])\s+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, 3);
};

const getIssueType = (item) => {
  const value = Number(item.calculatedValue);
  if (item.calculatedValue === null || item.calculatedValue === undefined) {
    return 'data_quality';
  }

  const standard = Number(item.standardValue);
  const good = item.higherIsBetter === false ? value <= standard : value >= standard;
  if (good) {
    return 'positive';
  }

  if (item.key === 'debt_to_equity_ratio' || item.key === 'debt_ratio') {
    return 'risk';
  }
  return 'performance';
};

const GaugeCard = ({ score, delta, verdict, language, t }) => {
  const angle = clamp(score, 0, 100) * 1.8;
  return (
    <article className="exec-card exec-gauge-card">
      <header>
        <span>{t('overallHealthScore')}</span>
        <strong>{formatNumber(score, language, 0)}</strong>
      </header>
      <div className="segmented-gauge" aria-hidden="true">
        <div className="needle" style={{ transform: `rotate(${angle - 90}deg)` }} />
        <div className="gauge-center">{formatNumber(score, language, 0)}</div>
      </div>
      <footer>
        <span className="delta-badge">{formatSigned(delta, language, 1)}</span>
        <span className={`verdict-badge verdict-${verdict.toLowerCase()}`}>{verdict}</span>
      </footer>
    </article>
  );
};

const ProgressBandCard = ({ above, total, language, t }) => {
  const percent = total > 0 ? (above / total) * 100 : 0;
  return (
    <article className="exec-card">
      <header>
        <span>{t('aboveStandardDensity')}</span>
        <strong>{above}/{total}</strong>
      </header>
      <div className="progress-band" role="img" aria-label={t('aboveStandardBand')}>
        <span className="progress-fill" style={{ width: `${clamp(percent, 0, 100)}%` }} />
      </div>
      <footer><span>{formatPercent(percent, language, 0)}</span></footer>
    </article>
  );
};

const ThermometerCard = ({ value, standard, language, t }) => {
  const ratio = Number.isNaN(value) || Number.isNaN(standard) || standard <= 0 ? 0 : clamp(value / (standard * 2), 0, 1);
  const level = Number.isNaN(value)
    ? t('noData')
    : value <= standard
      ? t('lowRisk')
      : value <= standard * 1.35
        ? t('caution')
        : t('alarm');

  return (
    <article className="exec-card">
      <header>
        <span>{t('leverageRisk')}</span>
        <strong>{level}</strong>
      </header>
      <div className="risk-thermometer" role="img" aria-label={t('leverageThermo')}>
        <span className="thermo-fill" style={{ height: `${ratio * 100}%` }} />
      </div>
      <footer>
        <span>{t('company')}: {formatNumber(value, language, 4)} | {t('standard')} {formatNumber(standard, language, 4)}</span>
      </footer>
    </article>
  );
};

const AiSnapshotCard = ({ score, verdict, criticalRisk, language, t }) => (
  <article className="exec-card">
    <header>
      <span>{t('aiExecutiveSnapshot')}</span>
      <strong>{verdict}</strong>
    </header>
    <div className="ai-mini-metrics">
      <div><small>{t('aiScore')}</small><b>{formatNumber(score, language, 0)}</b></div>
      <div><small>{t('verdictLabel')}</small><b>{verdict}</b></div>
      <div><small>{t('criticalRisk')}</small><b>{criticalRisk || '-'}</b></div>
    </div>
  </article>
);

const TrendChart = ({ points, standard, violations, language, t }) => {
  if (!points.length) {
    return <p className="dash-empty">{t('noTrendData')}</p>;
  }

  const width = 760;
  const height = 250;
  const left = 36;
  const right = 20;
  const top = 18;
  const bottom = 40;
  const w = width - left - right;
  const h = height - top - bottom;

  const values = points.map((p) => p.value);
  const allValues = [...values, ...points.map(() => Number(standard))];
  const min = Math.min(...allValues);
  const max = Math.max(...allValues);
  const range = Math.max(max - min, 0.000001);

  const xOf = (index) => left + (points.length === 1 ? w / 2 : (index / (points.length - 1)) * w);
  const yOf = (value) => top + ((max - value) / range) * h;

  const companyPath = points.map((p, i) => `${xOf(i)},${yOf(p.value)}`).join(' ');
  const standardPath = points.map((_, i) => `${xOf(i)},${yOf(Number(standard))}`).join(' ');

  return (
    <div className="trend-chart-wrap">
      <svg viewBox={`0 0 ${width} ${height}`} className="trend-chart" role="img" aria-label={t('periodTrendAnalysisAria')}>
        <line x1={left} y1={top} x2={left} y2={top + h} className="chart-axis" />
        <line x1={left} y1={top + h} x2={left + w} y2={top + h} className="chart-axis" />
        <polyline points={standardPath} className="chart-standard-line" />
        <polyline points={companyPath} className="chart-company-line" />
        {points.map((p, i) => {
          const isViolation = violations.has(p.periodCode);
          return (
            <g key={`${p.periodCode}-${i}`}>
              <circle cx={xOf(i)} cy={yOf(p.value)} r="4" className={isViolation ? 'chart-point-violation' : 'chart-point'} />
              <title>{`${p.periodCode} | ${t('company')} ${formatNumber(p.value, language, 4)} | ${t('standard')} ${formatNumber(standard, language, 4)} | ${t('diff')} ${formatPercent(p.deviation, language, 2)}`}</title>
            </g>
          );
        })}
      </svg>
    </div>
  );
};

function FinancialAnalysisDashboardPage() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useCallback((key) => UI_TEXT[language]?.[key] || UI_TEXT['tr-TR'][key] || key, [language]);
  const translateRatioTitle = useCallback((item) => {
    if (!item) {
      return '';
    }
    if (!language.startsWith('en')) {
      return item.title;
    }
    return RATIO_TITLE_TRANSLATIONS[item.key] || humanizeRatioKey(item.key) || translateApiText(item.title, language);
  }, [language]);
  const [selection, setSelection] = useState({ company: null, period: null, companyId: '', periodId: '' });
  const [orderedPeriods, setOrderedPeriods] = useState([]);
  const [loadingPeriods, setLoadingPeriods] = useState(false);

  const [loading, setLoading] = useState(false);
  const [basis, setBasis] = useState('IFRS');
  const [exportingPdf, setExportingPdf] = useState(false);
  const [message, setMessage] = useState('');
  const dashboardCaptureRef = useRef(null);

  const [standardData, setStandardData] = useState(null);
  const [trendData, setTrendData] = useState(null);
  const [sectorData, setSectorData] = useState(null);
  const [dashboardWarnings, setDashboardWarnings] = useState([]);
  const [savedAiReports, setSavedAiReports] = useState([]);
  const [latestAiReportDetail, setLatestAiReportDetail] = useState(null);
  const [trendRatioKey, setTrendRatioKey] = useState('');

  const hasSelection = Boolean(selection.companyId && selection.periodId);

  useEffect(() => {
    const fetchPeriods = async () => {
      setLoadingPeriods(true);
      try {
        const response = await fetch(`${API_BASE}/api/periods`, { headers: buildHeaders() });
        if (!response.ok) {
          throw new Error(t('periodsLoadError'));
        }
        const payload = await response.json();
        const ordered = [...(payload || [])].sort((left, right) => new Date(left.startDate || 0) - new Date(right.startDate || 0));
        setOrderedPeriods(ordered);
      } catch {
        setOrderedPeriods([]);
      } finally {
        setLoadingPeriods(false);
      }
    };
    fetchPeriods();
  }, [t]);

  const trendWindow = useMemo(() => {
    if (!selection.periodId || orderedPeriods.length === 0) {
      return null;
    }
    const endIndex = orderedPeriods.findIndex((item) => String(item.id) === String(selection.periodId));
    if (endIndex < 0) {
      return null;
    }
    const startIndex = Math.max(0, endIndex - 5);
    return {
      startPeriodId: orderedPeriods[startIndex]?.id,
      endPeriodId: orderedPeriods[endIndex]?.id,
      maxPeriods: Math.min(6, endIndex - startIndex + 1)
    };
  }, [selection.periodId, orderedPeriods]);

  const refreshDashboard = useCallback(async (companyId, periodId) => {
    if (!companyId || !periodId) {
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      const trendPromise = trendWindow
        ? fetchRatioTrend(companyId, trendWindow.startPeriodId, trendWindow.endPeriodId, trendWindow.maxPeriods, basis)
        : Promise.resolve(null);

      const [standardResult, trendResult, sectorResult, savedResult] = await Promise.allSettled([
        fetchStandardRatios(companyId, periodId, basis),
        trendPromise,
        fetchSectorRatioAnalysis(companyId, periodId, undefined, undefined, basis),
        fetchSavedAiFinancialReports(companyId, periodId)
      ]);

      if (standardResult.status !== 'fulfilled') {
        throw standardResult.reason;
      }

      const standardResponse = standardResult.value;
      const trendResponse = trendResult.status === 'fulfilled' ? trendResult.value : null;
      const sectorResponse = sectorResult.status === 'fulfilled' ? sectorResult.value : null;
      const savedResponse = savedResult.status === 'fulfilled' ? savedResult.value : [];
      const warnings = [];
      if (trendResult.status !== 'fulfilled') {
        warnings.push(`trend: ${trendResult.reason?.message || t('noTrendData')}`);
      }
      if (sectorResult.status !== 'fulfilled') {
        warnings.push(`sector: ${sectorResult.reason?.message || t('noSectorData')}`);
      }

      const savedRows = savedResponse || [];
      const latestSummary = [...savedRows].sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0))[0];
      let latestDetail = null;
      if (latestSummary?.id) {
        try {
          latestDetail = await fetchSavedAiFinancialReport(companyId, latestSummary.id);
        } catch {
          latestDetail = null;
        }
      }

      setStandardData(standardResponse);
      setTrendData(trendResponse);
      setSectorData(sectorResponse);
      setDashboardWarnings(warnings);
      setSavedAiReports(savedRows);
      setLatestAiReportDetail(latestDetail);
      setTrendRatioKey((prev) => prev || standardResponse?.items?.[0]?.key || '');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('dashboardLoadError'));
      setStandardData(null);
      setTrendData(null);
      setSectorData(null);
      setDashboardWarnings([]);
      setSavedAiReports([]);
      setLatestAiReportDetail(null);
    } finally {
      setLoading(false);
    }
  }, [basis, trendWindow, t]);

  useEffect(() => {
    if (!hasSelection) {
      setStandardData(null);
      setTrendData(null);
      setSectorData(null);
      setDashboardWarnings([]);
      setSavedAiReports([]);
      setLatestAiReportDetail(null);
      return;
    }
    refreshDashboard(selection.companyId, selection.periodId);
  }, [hasSelection, selection.companyId, selection.periodId, refreshDashboard]);

  const ratioRows = useMemo(() => standardData?.items || [], [standardData]);
  const issueRows = useMemo(() => ratioRows.map((item) => ({ ...item, issueType: getIssueType(item) })), [ratioRows]);

  const completionScore = useMemo(() => {
    const total = ratioRows.length;
    if (!total) {
      return 0;
    }
    const usable = total - Number(standardData?.noDataCount || 0);
    return clamp((usable / total) * 100, 0, 100);
  }, [ratioRows, standardData]);

  const healthScore = useMemo(() => {
    if (!ratioRows.length) {
      return 0;
    }
    const positive = issueRows.filter((item) => item.issueType === 'positive').length;
    const critical = issueRows.filter((item) => {
      if (item.issueType !== 'risk' && item.issueType !== 'performance') {
        return false;
      }
      return Math.abs(calculateDeviation(item.calculatedValue, item.standardValue) || 0) >= 25;
    }).length;
    const missing = issueRows.filter((item) => item.issueType === 'data_quality').length;
    return clamp((positive * 7) - (critical * 12) - (missing * 6) + 42, 0, 100);
  }, [ratioRows, issueRows]);

  const verdict = healthScore >= 75 ? t('strong') : healthScore >= 50 ? t('balanced') : t('risky');

  const trendHealthSeries = useMemo(() => {
    if (!trendData?.items?.length) {
      return [];
    }
    return trendData.items.map((row) => {
      const score = ratioRows.reduce((acc, item) => {
        const value = Number(row.metrics?.[item.key]);
        const standard = Number(item.standardValue);
        if (Number.isNaN(value) || Number.isNaN(standard)) {
          return acc;
        }
        const good = item.higherIsBetter === false ? value <= standard : value >= standard;
        return acc + (good ? 7 : -3);
      }, 42);
      return { periodCode: row.periodCode, score: clamp(score, 0, 100) };
    });
  }, [trendData, ratioRows]);

  const previousHealth = trendHealthSeries.length > 1 ? trendHealthSeries[trendHealthSeries.length - 2].score : healthScore;
  const healthDelta = healthScore - previousHealth;

  const criticalRiskItem = issueRows
    .filter((item) => item.issueType === 'risk' || item.issueType === 'performance')
    .sort((left, right) => (calculateDeviation(left.calculatedValue, left.standardValue) || 0) - (calculateDeviation(right.calculatedValue, right.standardValue) || 0))[0];
  const criticalRisk = translateRatioTitle(criticalRiskItem);

  const criticalDeviations = useMemo(() => {
    return issueRows
      .filter((item) => item.calculatedValue !== null && item.standardValue !== null)
      .map((item) => {
        const deviation = calculateDeviation(item.calculatedValue, item.standardValue) || 0;
        return { ...item, deviation, capped: clamp(deviation, -250, 250) };
      })
      .sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation))
      .slice(0, 5);
  }, [issueRows]);

  const standardRows = useMemo(() => {
    return issueRows
      .filter((item) => item.calculatedValue !== null && item.standardValue !== null)
      .map((item) => {
        const company = Number(item.calculatedValue || 0);
        const standard = Number(item.standardValue || 0);
        const domain = Math.max(company, standard, 0.0001) * 1.2;
        return {
          ...item,
          company,
          standard,
          domain,
          deviation: calculateDeviation(company, standard) || 0
        };
      })
      .sort((left, right) => Math.abs(right.deviation) - Math.abs(left.deviation));
  }, [issueRows]);

  const selectedTrendRatio = useMemo(
    () => ratioRows.find((item) => item.key === trendRatioKey) || ratioRows[0] || null,
    [ratioRows, trendRatioKey]
  );

  const trendPoints = useMemo(() => {
    if (!selectedTrendRatio?.key || !trendData?.items?.length) {
      return [];
    }
    return trendData.items
      .map((item) => {
        const value = Number(item.metrics?.[selectedTrendRatio.key]);
        if (Number.isNaN(value)) {
          return null;
        }
        return {
          periodCode: item.periodCode,
          value,
          deviation: calculateDeviation(value, selectedTrendRatio.standardValue)
        };
      })
      .filter(Boolean);
  }, [trendData, selectedTrendRatio]);

  const violationSet = useMemo(() => {
    const set = new Set();
    if (!selectedTrendRatio || !trendPoints.length) {
      return set;
    }
    const standard = Number(selectedTrendRatio.standardValue || 0);
    trendPoints.forEach((item) => {
      const violation = selectedTrendRatio.higherIsBetter === false ? item.value > standard : item.value < standard;
      if (violation) {
        set.add(item.periodCode);
      }
    });
    return set;
  }, [selectedTrendRatio, trendPoints]);

  const trendDirection = useMemo(() => {
    if (trendPoints.length < 2) {
      return t('flat');
    }
    const start = trendPoints[0].value;
    const end = trendPoints[trendPoints.length - 1].value;
    if (Math.abs(end - start) < 0.0001) {
      return t('flat');
    }
    return end > start ? t('strengthening') : t('weakening');
  }, [trendPoints, t]);

  const sectorRows = useMemo(() => {
    const standardMap = new Map(ratioRows.map((item) => [item.key, Number(item.standardValue || 0)]));
    return (sectorData?.items || []).map((item) => {
      const company = Number(item.calculatedValue || 0);
      const sector = Number(item.sectorValue || 0);
      const standard = standardMap.get(item.key) || 0;
      const domain = Math.max(company, sector, standard, 0.0001) * 1.2;
      const percentile = sector > 0 ? clamp((company / (sector * 1.4)) * 100, 1, 99) : null;
      return {
        ...item,
        company,
        sector,
        standard,
        domain,
        percentile
      };
    });
  }, [sectorData, ratioRows]);

  const aiLines = useMemo(() => {
    const lines = summarizeAiText(latestAiReportDetail?.reportText);
    return lines.map((line) => translateApiText(line, language));
  }, [latestAiReportDetail, language]);
  const aiConfidence = clamp(completionScore - (issueRows.filter((item) => item.issueType === 'data_quality').length * 4), 0, 100);

  const strengthRows = issueRows.filter((item) => item.issueType === 'positive').slice(0, 4);
  const weaknessRows = issueRows.filter((item) => item.issueType === 'risk' || item.issueType === 'performance').slice(0, 4);

  const actionRows = weaknessRows.slice(0, 3).map((item, index) => {
    const deviation = Math.abs(calculateDeviation(item.calculatedValue, item.standardValue) || 0);
    return {
      key: item.key,
      title: translateRatioTitle(item),
      priority: index === 0 ? 'P1' : index === 1 ? 'P2' : 'P3',
      impact: Math.max(2, Math.round(deviation / 12))
    };
  });

  const suspiciousCount = issueRows.filter((item) => Math.abs(calculateDeviation(item.calculatedValue, item.standardValue) || 0) > 300).length;
  const missingCount = issueRows.filter((item) => item.issueType === 'data_quality').length;
  const dataTrustScore = clamp(100 - (missingCount * 10) - (suspiciousCount * 6), 0, 100);

  const debtRatioItem = ratioRows.find((item) => item.key === 'debt_to_equity_ratio');

  const handleExportPdf = useCallback(async () => {
    if (!dashboardCaptureRef.current || exportingPdf) {
      return;
    }

    try {
      setExportingPdf(true);
      await exportDashboardPdf({
        element: dashboardCaptureRef.current,
        title: t('pageTitle'),
        companyName: selection.company?.name || selection.company?.title || '',
        periodLabel: selection.period?.label || selection.period?.name || '',
        language
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : t('dashboardLoadError'));
    } finally {
      setExportingPdf(false);
    }
  }, [exportingPdf, language, selection.company, selection.period, t]);

  return (
    <div className="card financial-analysis-dashboard-card" ref={dashboardCaptureRef}>
      <PageHeader
        title={t('pageTitle')}
        description={t('pageDescription')}
        actions={
          <div className="financial-dashboard-page-actions" data-export-hidden="true">
            <button
              type="button"
              className="btn-secondary"
              onClick={handleExportPdf}
              disabled={!hasSelection || loading || loadingPeriods || exportingPdf}
            >
              {exportingPdf ? t('exportingPdf') : t('exportPdf')}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => refreshDashboard(selection.companyId, selection.periodId)}
              disabled={!hasSelection || loading || loadingPeriods || exportingPdf}
            >
              {loading ? t('refreshing') : t('refreshDashboard')}
            </button>
          </div>
        }
      />

      <CompanyPeriodSelector onSelectionChange={setSelection} locale={language} />
      <section className="dashboard-section" data-export-hidden="true">
        <div className="dashboard-inline-control">
          <label htmlFor="dashboard-basis-select">{t('reportingBasis')}</label>
          <select id="dashboard-basis-select" value={basis} onChange={(event) => setBasis(event.target.value)}>
            <option value="IFRS">{t('ifrsDefault')}</option>
            <option value="VUK">{t('vukOption')}</option>
          </select>
        </div>
      </section>

      {message && <p className="financial-analysis-dashboard-message">{message}</p>}
      {!message && <p className="financial-analysis-dashboard-message">{t('activeBasis')}: {basis}</p>}
      {!message && basis === 'IFRS' && <p className="financial-analysis-dashboard-message">{t('basisMismatchNotice')}</p>}
      {!message && dashboardWarnings.length > 0 && <div className="financial-analysis-dashboard-message">{dashboardWarnings.join(' | ')}</div>}

      <section className="executive-top-grid">
        <GaugeCard score={healthScore} delta={healthDelta} verdict={verdict} language={language} t={t} />
        <ProgressBandCard above={standardData?.aboveCount || 0} total={ratioRows.length} language={language} t={t} />
        <ThermometerCard value={Number(debtRatioItem?.calculatedValue)} standard={Number(debtRatioItem?.standardValue)} language={language} t={t} />
        <AiSnapshotCard score={healthScore} verdict={verdict} criticalRisk={criticalRisk} language={language} t={t} />
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('criticalDeviations')}</h3>
          <span>{t('top5Deviation')}</span>
        </div>
        <div className="diverging-list">
          {criticalDeviations.length === 0 ? <p className="dash-empty">{t('noDeviationData')}</p> : criticalDeviations.map((item) => (
            <article key={item.key} className="diverging-row">
              <div className="diverging-head">
                <strong>{translateRatioTitle(item)}</strong>
                <span>{formatSigned(item.deviation, language, 1)}%</span>
              </div>
              <div className="diverging-track">
                <span className="diverging-center" />
                <span className={`diverging-bar ${item.capped >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(item.capped) / 5)}%`, [item.capped >= 0 ? 'left' : 'right']: '50%' }} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('companyVsStandard')}</h3>
          <span>{t('sortedByDeviation')}</span>
        </div>
        <div className="bullet-list">
          {!standardRows.length ? <p className="dash-empty">{t('noCompareData')}</p> : standardRows.map((item) => (
            <article key={item.key} className="bullet-row">
              <div className="bullet-head"><strong>{translateRatioTitle(item)}</strong><span>{t('company')} {formatNumber(item.company, language, 4)} | {t('standard')} {formatNumber(item.standard, language, 4)} | {t('diff')} {formatSigned(item.deviation, language, 1)}%</span></div>
              <div className="bullet-track">
                <span className="bullet-zones" />
                <span className="bullet-company" style={{ width: `${clamp((item.company / item.domain) * 100, 0, 100)}%` }} />
                <span className="bullet-standard" style={{ left: `${clamp((item.standard / item.domain) * 100, 0, 100)}%` }} />
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('periodTrendAnalysis')}</h3>
          <div className="dashboard-inline-control">
            <label htmlFor="trend-ratio-key">{t('ratio')}</label>
            <select id="trend-ratio-key" value={selectedTrendRatio?.key || ''} onChange={(event) => setTrendRatioKey(event.target.value)}>
              {ratioRows.map((item) => <option key={item.key} value={item.key}>{translateRatioTitle(item)}</option>)}
            </select>
          </div>
        </div>
        <TrendChart points={trendPoints} standard={selectedTrendRatio?.standardValue} violations={violationSet} language={language} t={t} />
        <div className="trend-badge-row">
          <span className={`trend-badge ${trendDirection === t('strengthening') ? 'positive' : trendDirection === t('weakening') ? 'negative' : 'neutral'}`}>
            {trendDirection === t('strengthening')
              ? `^ ${t('strengthening')}`
              : trendDirection === t('weakening')
                ? `v ${t('weakening')}`
                : `> ${t('flat')}`}
          </span>
          <span>{trendData?.items?.[0]?.periodCode || '-'} - {trendData?.items?.[trendData?.items?.length - 1]?.periodCode || '-'}</span>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('sectorBenchmark')}</h3>
          <span>{translateApiText(sectorData?.sectorName, language) || '-'}</span>
        </div>
        <div className="sector-compare-list">
          {!sectorRows.length ? <p className="dash-empty">{t('noSectorData')}</p> : sectorRows.map((item) => (
            <article key={item.key} className="sector-compare-row">
              <div className="sector-title"><strong>{translateRatioTitle(item)}</strong><span>{item.percentile ? t('percentileInSector').replace('{pct}', formatNumber(item.percentile, language, 0)) : '-'}</span></div>
              <div className="tri-bar-group">
                <div><small>{t('company')}</small><span style={{ width: `${clamp((item.company / item.domain) * 100, 0, 100)}%` }} /></div>
                <div><small>{t('sectorAvg')}</small><span style={{ width: `${clamp((item.sector / item.domain) * 100, 0, 100)}%` }} /></div>
                <div><small>{t('standard')}</small><span style={{ width: `${clamp((item.standard / item.domain) * 100, 0, 100)}%` }} /></div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('aiVerdictAndAction')}</h3>
          <span>{t('lastAiReport')}: {translateApiText(latestAiReportDetail?.modelName, language) || '-'} | {formatDateTime(latestAiReportDetail?.createdAt, language)}</span>
        </div>

        <div className="ai-detail-grid">
          <article className="ai-panel">
            <h4>{t('aiVerdictCard')}</h4>
            <div className="ai-verdict-row">
              <span className={`verdict-badge verdict-${verdict.toLowerCase()}`}>{verdict}</span>
              <span>{t('confidence')}: {formatPercent(aiConfidence, language, 0)}</span>
            </div>
            <p>{t('mostCriticalRisk')}: <strong>{criticalRisk || '-'}</strong></p>
            <div className="ai-text-lines">
              {aiLines.length ? aiLines.map((line, index) => <p key={`${index}-${line.slice(0, 10)}`}>{line}</p>) : <p className="dash-empty">{t('aiReportTextMissing')}</p>}
            </div>
          </article>

          <article className="ai-panel">
            <h4>{t('driverAnalysis')}</h4>
            <div className="driver-diverging-list">
              {criticalDeviations.map((item) => (
                <div key={item.key} className="driver-diverging-row">
                  <span>{translateRatioTitle(item)}</span>
                  <div className="driver-diverging-track">
                    <span className="driver-center" />
                    <span className={`driver-bar ${item.capped >= 0 ? 'positive' : 'negative'}`} style={{ width: `${Math.min(50, Math.abs(item.capped) / 5)}%`, [item.capped >= 0 ? 'left' : 'right']: '50%' }} />
                  </div>
                </div>
              ))}
            </div>
          </article>

          <article className="ai-panel">
            <h4>{t('strengthVsWeakness')}</h4>
            <div className="strength-weak-grid">
              <div>
                <strong>{t('strong')}</strong>
                {strengthRows.map((item) => {
                  const deviation = calculateDeviation(item.calculatedValue, item.standardValue);
                  return <p key={item.key}>{translateRatioTitle(item)} <b>{formatSigned(deviation, language, 1)}%</b></p>;
                })}
              </div>
              <div>
                <strong>{t('weaknesses')}</strong>
                {weaknessRows.map((item) => {
                  const deviation = calculateDeviation(item.calculatedValue, item.standardValue);
                  return <p key={item.key}>{translateRatioTitle(item)} <b>{formatSigned(deviation, language, 1)}%</b></p>;
                })}
              </div>
            </div>
          </article>

          <article className="ai-panel">
            <h4>{t('actionImpactProjection')}</h4>
            <div className="impact-list">
              {actionRows.length ? actionRows.map((item) => (
                <div key={item.key} className="impact-row">
                  <div className="impact-head">
                    <span>{item.priority}</span>
                    <strong>{translateApiText(item.title, language)}</strong>
                    <b>{t('scoreBoost')}{item.impact}</b>
                  </div>
                  <div className="impact-bar"><span style={{ width: `${clamp(item.impact * 8, 0, 100)}%` }} /></div>
                </div>
              )) : <p className="dash-empty">{t('actionPriorityMissing')}</p>}
            </div>
          </article>
        </div>
      </section>

      <section className="dashboard-section">
        <div className="dashboard-section-head">
          <h3>{t('dataQualityIndicator')}</h3>
          <span>{t('trustScore')} {formatPercent(dataTrustScore, language, 0)}</span>
        </div>
        <div className="data-quality-grid">
          <article><span>{t('missingData')}</span><strong>{missingCount}</strong></article>
          <article><span>{t('suspiciousData')}</span><strong>{suspiciousCount}</strong></article>
          <article><span>{t('trustScore')}</span><strong>{formatPercent(dataTrustScore, language, 0)}</strong></article>
          <article><span>{t('aiRecordCount')}</span><strong>{savedAiReports.length}</strong></article>
        </div>
      </section>

      <section className="financial-analysis-dashboard-links">
        <button type="button" className="btn-secondary" onClick={() => navigate('/analysis/ratios-standard')}>{t('standardAnalysis')}</button>
        <button type="button" className="btn-secondary" onClick={() => navigate('/analysis/ratio-trend')}>{t('trendAnalysis')}</button>
        <button type="button" className="btn-secondary" onClick={() => navigate('/analysis/sector-ratio')}>{t('sectorAnalysis')}</button>
        <button type="button" className="btn-secondary" onClick={() => navigate('/analysis/ai-report')}>{t('aiReportDetail')}</button>
      </section>
    </div>
  );
}

export default FinancialAnalysisDashboardPage;

